import React, { useState, useRef, useMemo } from "react";
import { ContentHeader } from "@components";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import DataTable from "../components/datatable/DataTable";
import PatientModal from "../components/modal/PatientModal";
import API from "../api";
import toastr from "toastr";

interface Patient {
  id?: number;
  P_code: string;
  P_name: string;
  prefix: string;
  gender: string;
  age: string;
  age_type: string;
  mobile: string;
  dob: string;
  blood_group: string;
  ob_number: string;
  room_number: string;
  id_card_type: string;
  id_number: string;
  email: string;
  address: string;
}

const Patients: React.FC = () => {
  const { user } = useAuth();
  const permissions = user?.permissions || {};
  const navigate = useNavigate();

  const [showModal, setShowModal]           = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [modalMode, setModalMode]           = useState<"add" | "edit">("add");
  const tableRef = useRef<any>(null);

  const fetchPatients = async (
    page: number,
    search: string,
    sortBy = "id",
    sortOrder = "desc"
  ) => {
    try {
      const res = await API.get(
        `/patients/getPatients?page=${page}&pageSize=25&search=${search}&sortBy=${sortBy}&sortOrder=${sortOrder}`
      );
      return { data: res.data.data, total: res.data.total };
    } catch {
      toastr.error("Failed to fetch patients");
      return { data: [], total: 0 };
    }
  };

  const handleAdd = () => {
    setModalMode("add");
    setSelectedPatient(null);
    setShowModal(true);
  };

  const handleEdit = (patient: Patient) => {
    setModalMode("edit");
    setSelectedPatient(patient);
    setShowModal(true);
  };

  // Flask icon → Patient Entry (matches route: patientfilter/:id)
  const handlePatientEntry = (id: number) => {
    navigate(`/patientfilter/${id}`);
  };

  // Address Book icon → Patient Log (matches route: patientlog/:id)
  const handlePatientLog = (id: number) => {
    navigate(`/patientlog/${id}`);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this patient?")) return;
    try {
      await API.delete(`/patients/deletePatient/${id}`);
      toastr.success("Patient deleted successfully");
      tableRef.current?.refetch();
    } catch {
      toastr.error("Failed to delete patient");
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedPatient(null);
  };

  const columns = useMemo(
    () => [
      { header: "Code", accessorKey: "P_code" },
      {
        header: "Patient Name",
        id: "name_display",
        cell: ({ row }: any) => `${row.original.prefix}.${row.original.P_name}`,
      },
      { header: "Gender", accessorKey: "gender" },
      {
        header: "Age",
        id: "age_display",
        cell: ({ row }: any) => `${row.original.age} ${row.original.age_type}`,
      },
      { header: "Mobile", accessorKey: "mobile" },
      { header: "Blood Group", accessorKey: "blood_group" },
      {
        header: "Actions",
        id: "actions",
        cell: ({ row }: any) => {
          const data: Patient = row.original;
          return (
            <div className="d-flex justify-content-center gap-2">
              {permissions.editpatient && (
                <button
                  className="btn btn-light"
                  onClick={() => handleEdit(data)}
                  title="Edit"
                >
                  <i className="fa fa-edit" />
                </button>
              )}

              {permissions.patients && (
                <button
                  className="btn btn-light"
                  onClick={() => handlePatientEntry(data.id!)}
                  title="Patient Entry"
                >
                  <i className="fa fa-flask" />
                </button>
              )}

              {permissions.patients && (
                <button
                  className="btn btn-light"
                  onClick={() => handlePatientLog(data.id!)}
                  title="Patient Log"
                >
                  <i className="fa fa-address-book" />
                </button>
              )}

              {permissions.deletepatient && (
                <button
                  className="btn btn-light"
                  onClick={() => handleDelete(data.id!)}
                  title="Delete"
                >
                  <i className="fa fa-trash" />
                </button>
              )}
            </div>
          );
        },
      },
    ],
    [permissions]
  );

  // ── If user does not have patients view permission, show access denied ──
  if (!permissions.patients) {
    return (
      <div>
        <ContentHeader title="Patients" />
        <section className="content">
          <div className="container-fluid">
            <div className="card">
              <div className="card-body text-center py-5">
                <i className="fas fa-lock fa-3x text-muted mb-3"></i>
                <h4 className="text-muted">Access Denied</h4>
                <p className="text-muted">You do not have permission to view patients.</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center">
        <ContentHeader title="Patients" />
        {permissions.addpatient && (
          <button className="btn btn-light px-5" onClick={handleAdd}>
            <i className="fa fa-plus" /> Add Patient
          </button>
        )}
      </div>

      <section className="content">
        <div className="container-fluid">
          <div className="card card-outline card-primary">
            <div className="card-body">
              <DataTable
                ref={tableRef}
                columns={columns}
                queryFn={fetchPatients}
                queryKey={["patients"]}
              />
            </div>
          </div>
        </div>
      </section>

      {showModal && (
        <PatientModal
          show={showModal}
          onClose={handleModalClose}
          mode={modalMode}
          patientData={selectedPatient}
          onSuccess={() => {
            handleModalClose();
            tableRef.current?.refetch();
          }}
        />
      )}
    </div>
  );
};

export default Patients;